import { api, getAccessToken, type DocumentRegister, type RegisterDocument } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { openPdfViewer } from './pdf-viewer';
import { esc, html, toast } from '../ui';

/**
 * Uploading a record.
 *
 * The file is optional on purpose: plenty of documents exist as a register
 * entry — a manual held on the aircraft, a contract in a filing cabinet —
 * long before anyone scans them, and a register that refuses to record those
 * is a register nobody keeps up.
 */

export const fmtBytes = (n: number): string =>
  n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.txt,.csv,.xml,.doc,.docx,.xls,.xlsx';

function fileField(label: string, hint: string): string {
  return html`
    <div class="field">
      <label>${esc(label)}</label>
      <input type="file" name="file" accept="${ACCEPT}">
      <span class="hint">${esc(hint)}</span>
    </div>
  `;
}

/** Reads the chosen file out of a rendered dialog body. */
function chosenFile(root: HTMLElement): File | null {
  return root.querySelector<HTMLInputElement>('[name="file"]')?.files?.[0] ?? null;
}

export function uploadDocument(
  reg: DocumentRegister,
  opts: { aircraftId?: string; registration?: string },
  reload: () => void,
): void {
  const kinds = reg.kinds.filter((k) =>
    opts.aircraftId ? k.level !== 'ORGANISATION' : k.level !== 'AIRCRAFT',
  );

  openDialog<FormData>({
    wide: true,
    title: 'Add document',
    sub: opts.registration
      ? `Filed against ${opts.registration}`
      : 'Filed against the organisation — exposition, procedures, contracts',
    primary: 'Add to the register',
    body: () => html`
      <div class="frow">
        ${field('Kind', select('kind', kinds.map((k) => opt(k.kind, k.label)).join('')))}
        ${field('Title', input('title', '', 'maxlength="300"'))}
      </div>
      <div class="frow3">
        ${field('Reference', input('reference', '', 'maxlength="80"'))}
        ${field('Revision', input('revision', '', 'placeholder="Rev 7" maxlength="40"'))}
        ${field('Revision date', input('revisionDate', '', 'type="date"'))}
      </div>
      ${field('Next revision check due', input('nextCheckDue', '', 'type="date"'),
        'The register flags it when the check falls due')}
      ${fileField('File', 'PDF, image, or office document. Optional — the entry can be recorded now and scanned later.')}
      ${callout('info', 'i', html`
        Files are stored on the server in this organisation's own folder, hashed on the way in so the
        register can prove later that what is stored is still what was filed.
      `)}
    `,
    collect: (root) => {
      const title = val(root, 'title');
      if (!title) return { error: 'Give the document a title.' };

      const form = new FormData();
      form.append('kind', val(root, 'kind'));
      form.append('title', title);
      for (const name of ['reference', 'revision', 'revisionDate', 'nextCheckDue']) {
        const v = val(root, name);
        if (v) form.append(name, v);
      }
      if (opts.aircraftId) form.append('aircraftId', opts.aircraftId);

      const file = chosenFile(root);
      if (file) form.append('file', file, file.name);
      return form;
    },
    submit: (form) => api.documents.create(form),
    done: (r) =>
      r.attachment
        ? `${r.title} added — ${r.attachment.fileName} (${fmtBytes(r.attachment.byteSize)}) stored`
        : `${r.title} added to the register`,
    onDone: reload,
  });
}

/** A newer revision. The old one is kept and marked superseded. */
export function reviseDocument(doc: RegisterDocument, reload: () => void): void {
  openDialog<FormData>({
    wide: true,
    title: 'New revision',
    sub: `${doc.title}${doc.revision ? ` — currently ${doc.revision}` : ''}`,
    primary: 'File the revision',
    body: () => html`
      <div class="frow3">
        ${field('Revision', input('revision', '', 'placeholder="Rev 8" maxlength="40"'))}
        ${field('Revision date', input('revisionDate', '', 'type="date"'))}
        ${field('Next check due', input('nextCheckDue', '', 'type="date"'))}
      </div>
      ${fileField('File', 'Optional — the entry can be recorded now and the file added later.')}
      ${callout('info', 'i', html`
        ${esc(doc.revision ?? 'The current entry')} is kept and marked superseded rather than replaced. Any
        signature made while it was current still points at the file that was in force at the time.
      `)}
    `,
    collect: (root) => {
      const form = new FormData();
      form.append('kind', doc.kind);
      form.append('title', doc.title);
      for (const name of ['revision', 'revisionDate', 'nextCheckDue']) {
        const v = val(root, name);
        if (v) form.append(name, v);
      }
      const file = chosenFile(root);
      if (file) form.append('file', file, file.name);
      return form;
    },
    submit: (form) => api.documents.revise(doc.id, form),
    done: () => `${doc.title} superseded`,
    onDone: reload,
  });
}

/** Re-hashes stored files and reports anything missing or altered. */
export async function verifyIntegrity(): Promise<void> {
  try {
    const r = await api.documents.verify();
    if (r.missing || r.altered) {
      toast(
        `${r.checked} checked — ${r.missing} missing, ${r.altered} altered. ${r.problems[0]?.fileName ?? ''}`,
        'error',
      );
    } else {
      toast(`${r.checked} file(s) checked, all intact`);
    }
  } catch (e) {
    toast(e instanceof Error ? e.message : 'Could not run the check.', 'error');
  }
}

/**
 * The document's title, written so that clicking it opens the document.
 *
 * Everywhere else in the application a document is read in the viewer; on the
 * register the only thing to click was the file size, and that downloaded it.
 * Reading the manual you have just filed should not mean fetching a copy of it
 * first. Anything that is not a PDF stays plain text — the viewer would show a
 * blank frame — and the download button beside it is still the way to get it.
 */
export function docTitle(d: RegisterDocument, label?: string): string {
  const text = label ?? d.title;
  if (!d.attachment || !d.attachment.mimeType.includes('pdf')) return esc(text);
  const sub = [d.reference, d.revision ? `revision ${d.revision}` : null].filter(Boolean).join(' · ');
  return html`<button type="button" class="docopen" data-view="${esc(d.attachment.id)}"
                      data-title="${esc(d.title)}" data-sub="${esc(sub)}"
                      title="Open the document">${esc(text)}</button>`;
}

/**
 * Removing an entry from the register.
 *
 * Deliberately not offered on a superseded revision: that one is history, and
 * the file behind it is what a past signature was made against. What this is
 * for is the entry that should never have been there — the manual uploaded
 * twice, the wrong file picked.
 */
export function removeDocument(d: RegisterDocument, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Remove this document',
    sub: d.title,
    primary: 'Remove it',
    body: () => html`
      ${callout('caution', '!', html`
        ${d.attachment
          ? html`<b>${esc(d.attachment.fileName)}</b> is deleted from storage as well, unless another
                 record still points at it.`
          : 'This entry has no file — only the register entry goes.'}
        <br><br>
        A document a certificate or a reading was made against cannot be removed; the refusal says which
        record holds it. To replace a manual with a newer issue, use <b>Revise</b> instead — that keeps
        the old file retrievable, which is what a past signature needs.
      `)}
    `,
    collect: () => ({}),
    submit: () => api.documents.remove(d.id),
    done: (r) => `${r.title} removed${r.fileRemoved ? ' — file deleted' : ' — file kept, another record points at it'}`,
    onDone: reload,
  });
}

/** Wires every [data-view] title on a rendered screen — opens it in the viewer. */
export function bindDocumentViewers(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-view]').forEach((b) =>
    b.addEventListener('click', () =>
      openPdfViewer({
        path: `/attachments/${b.dataset.view}/download`,
        title: b.dataset.title ?? 'Document',
        subtitle: b.dataset.sub || undefined,
        accessToken: getAccessToken,
      }),
    ),
  );
}

/** Wires every [data-download] button on a rendered screen. */
export function bindDownloads(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-download]').forEach((b) =>
    b.addEventListener('click', async () => {
      const label = b.textContent;
      b.textContent = '…';
      try {
        await api.documents.download(b.dataset.download!, b.dataset.name ?? 'document');
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Could not download that file.', 'error');
      } finally {
        b.textContent = label;
      }
    }),
  );
}
